/**
 * Checks if the provided value is of type number and is not undefined or NaN.
 * Should be used when you want an actual, defined, real number with a value.
 *
 * @param input - The value to check for validity as a number.
 * @returns True if the value is of type number and is not undefined or NaN, false otherwise.
 */
export function isValidNum(input: unknown): input is number {
    if (typeof input === "number") {
        return !Number.isNaN(input);
    }
    return false;
}

/**
 * Returns the value, unless the value is outside the range specified by min max,
 * in which case it returns either min or max based on which is closer
 * @param value
 * @param min
 * @param max
 * @returns {number}
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Computes the logarithm of a value with a specified base.
 * Uses the change of base formula: log_b(x) = log(x) / log(b)
 *
 * @param value - The number to compute the logarithm of (must be positive)
 * @param base - The base of the logarithm (must be positive and not equal to 1)
 * @returns The logarithm of value with the specified base
 * @throws {Error} If value is not positive or base is not positive or is 1
 */
export function logBase(value: number, base: number): number {
    if (value <= 0) {
        throw new Error(`Cannot compute logarithm of non-positive value: ${value}`);
    }
    if (base <= 0 || base === 1) {
        throw new Error(`Invalid logarithm base: ${base}. Base must be positive and not equal to 1.`);
    }
    return Math.log(value) / Math.log(base);
}

/** Cumulative binomial distribution: index k is P(X ≤ k) for X ~ Binomial(n, probability). */
export function binomialCumulativeDistribution(n: number, probability: number): number[] {
    let binomialCoefficient = 1;
    const distribution: number[] = [];

    for (let successes = 0; successes <= n; successes++) {
        distribution.push(binomialCoefficient * probability ** successes * (1 - probability) ** (n - successes));
        const nextSuccesses = successes + 1;
        if (nextSuccesses <= n) {
            binomialCoefficient = (binomialCoefficient * (n + 1 - nextSuccesses)) / nextSuccesses;
        }
    }

    let cumulativeSum = 0;
    return distribution.map((probabilityMass) => {
        cumulativeSum += probabilityMass;
        return cumulativeSum;
    });
}
